import { useEffect, useRef, useState } from "react";
import { AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, Italic, Underline, X } from "lucide-react";
import { TEXT_FONTS } from "@/lib/blocks";

const STYLE_KEYS = ["size", "font", "color", "bg", "bold", "italic", "underline", "align", "leading"];
const ALIGN = [["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight], ["justify", AlignJustify]];
const LEADING = [["", "Normal"], ["1", "Tight (1)"], ["1.25", "1.25"], ["1.5", "1.5"], ["1.75", "1.75"], ["2", "Double (2)"], ["2.5", "2.5"], ["3", "3"]];
const lbl = "mb-1 block font-ui text-[10px] uppercase tracking-widest text-[#5F6875]";
const sel = "w-full rounded-lg border border-[#14213D]/15 bg-white px-2 py-1.5 font-ui text-xs text-[#14213D] outline-none focus:border-[#C8862F]";

const pickStyle = (b) => Object.fromEntries(STYLE_KEYS.map((k) => [k, b[k] ?? ""]));

const Toggle = ({ on, onClick, label, Icon, testid }) => (
  <button type="button" data-testid={testid} aria-label={label} aria-pressed={on} title={label} onClick={onClick}
    className={`grid h-8 w-8 place-items-center rounded-lg border transition-colors ${on ? "border-[#C8862F] bg-[#C8862F]/15 text-[#14213D]" : "border-[#14213D]/15 bg-white text-[#5F6875] hover:bg-[#14213D]/5"}`}>
    <Icon className="h-4 w-4" />
  </button>
);

const Color = ({ label, value, onChange, testid }) => (
  <div>
    <span className={lbl}>{label}</span>
    <div className="flex items-center gap-1.5">
      <input type="color" data-testid={testid} value={value || "#14213d"} onChange={(e) => onChange(e.target.value)} className="h-8 w-10 cursor-pointer rounded border border-[#14213D]/15 bg-white p-0.5" />
      <span className="font-mono text-[11px] text-[#4A5568]">{value || "Default"}</span>
      {value && <button type="button" data-testid={`${testid}-clear`} onClick={() => onChange("")} aria-label={`Reset ${label}`} className="rounded-full p-1 text-[#5F6875] hover:bg-[#14213D]/5"><X className="h-3 w-3" /></button>}
    </div>
  </div>
);

// Look of a text block: size, font, colors, bold/italic/underline, alignment and line spacing.
// Changes wait a moment before saving, so dragging the size slider or color picker doesn't send dozens of saves.
export const TextStyle = ({ b, onChange }) => {
  const [s, setS] = useState(() => pickStyle(b));
  const latest = useRef(b);
  latest.current = b;
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    const t = setTimeout(() => {
      const nb = { ...latest.current };
      STYLE_KEYS.forEach((k) => { if (s[k] === "" || s[k] === false) delete nb[k]; else nb[k] = s[k]; });
      onChange(nb);
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const t = `ve-style-${b.id}`;
  return (
    <div data-testid={t} className="grid gap-3 rounded-xl bg-[#14213D]/[0.03] p-3">
      <p className="font-ui text-[11px] font-semibold uppercase tracking-widest text-[#8A6A12]">Text style</p>
      <div>
        <span className={lbl}>Font size: {s.size ? `${s.size}px` : "Normal"}</span>
        <div className="flex items-center gap-2">
          <input type="range" min="12" max="72" step="1" data-testid={`${t}-size`} value={s.size || 18} onChange={(e) => set("size", Number(e.target.value))} className="w-full accent-[#C8862F]" />
          {s.size !== "" && <button type="button" data-testid={`${t}-size-clear`} onClick={() => set("size", "")} className="font-ui text-[11px] text-[#5F6875] underline">Reset</button>}
        </div>
      </div>
      <label className="block">
        <span className={lbl}>Font style</span>
        <select data-testid={`${t}-font`} value={s.font} onChange={(e) => set("font", e.target.value)} className={sel} style={s.font ? { fontFamily: `"${s.font}", serif` } : undefined}>
          <option value="">Website default</option>
          {TEXT_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <Color label="Font color" value={s.color} onChange={(v) => set("color", v)} testid={`${t}-color`} />
        <Color label="Background" value={s.bg} onChange={(v) => set("bg", v)} testid={`${t}-bg`} />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <span className={lbl}>Emphasis</span>
          <div className="flex gap-1">
            <Toggle on={!!s.bold} onClick={() => set("bold", !s.bold)} label="Bold" Icon={Bold} testid={`${t}-bold`} />
            <Toggle on={!!s.italic} onClick={() => set("italic", !s.italic)} label="Italic" Icon={Italic} testid={`${t}-italic`} />
            <Toggle on={!!s.underline} onClick={() => set("underline", !s.underline)} label="Underline" Icon={Underline} testid={`${t}-underline`} />
          </div>
        </div>
        <div>
          <span className={lbl}>Alignment</span>
          <div className="flex gap-1">
            {ALIGN.map(([a, Icon]) => <Toggle key={a} on={s.align === a} onClick={() => set("align", s.align === a ? "" : a)} label={`Align ${a}`} Icon={Icon} testid={`${t}-align-${a}`} />)}
          </div>
        </div>
      </div>
      <label className="block">
        <span className={lbl}>Line spacing</span>
        <select data-testid={`${t}-leading`} value={String(s.leading)} onChange={(e) => set("leading", e.target.value ? Number(e.target.value) : "")} className={sel}>
          {LEADING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
    </div>
  );
};
